/** Money, percentages and dates, the same way on every screen. */

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
const usdWhole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
const usdCompact = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 })

/** 123456 → "$1,234.56". Every amount the API sends is in cents, USD. */
export function money(cents: number | null | undefined): string {
  return usd.format((cents ?? 0) / 100)
}

/** Whole dollars when there are no cents: 6900 → "$69", 5865 → "$58.65". */
export function moneyShort(cents: number): string {
  return cents % 100 === 0 ? usdWhole.format(cents / 100) : usd.format(cents / 100)
}

/** Chart axis labels: 125000 → "$1.3K". */
export function moneyAxis(cents: number): string {
  return cents >= 100_000 ? usdCompact.format(cents / 100) : usdWhole.format(cents / 100)
}

/** 15 → "15%", 12.5 → "12.5%". */
export function pct(value: number | null | undefined): string {
  const n = value ?? 0
  return `${Number.isInteger(n) ? n : n.toFixed(1).replace(/\.0$/, '')}%`
}

export function count(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US').format(value ?? 0)
}

export function date(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' }).format(d)
}

/** "2026-10-03" (a calendar day from the API) → "3 Oct", without a timezone shift. */
export function day(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)))
}

export function humanise(value: string | null | undefined): string {
  if (!value) return ''
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
